/**
 * Remembered notification preferences (vc.notificationPrefs): whether the client-side
 * calendar reminder loop runs at all and how long before an event's start it fires.
 * A dedicated small key rather than devicePrefs fields: the values are reminder-surface
 * state, not device state, and every field is validated on load - anything unknown
 * degrades to its default without rejecting the rest of the document.
 */

import type { ViewPrefStorage } from './viewPref';

export const NOTIFICATION_PREFS_STORAGE_KEY = 'vc.notificationPrefs';

/** How long before an event's start the reminder fires. */
export type LeadMinutes = 5 | 10 | 15;

export interface NotificationPrefs {
  readonly enabled: boolean;
  readonly leadMinutes: LeadMinutes;
}

export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  enabled: false,
  leadMinutes: 10,
};

/** The minimal storage surface the preferences need (structural - the KeyValueStorage the
 * settings page uses and the DOM Storage both satisfy it). */
export type NotificationPrefsStorage = ViewPrefStorage;

/** The lead options the settings select offers; kept as a tuple for the switcher order. */
export const KNOWN_LEAD_MINUTES: readonly LeadMinutes[] = [5, 10, 15];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readEnabled(value: unknown): boolean {
  return typeof value === 'boolean' ? value : DEFAULT_NOTIFICATION_PREFS.enabled;
}

function readLeadMinutes(value: unknown): LeadMinutes {
  const match = KNOWN_LEAD_MINUTES.find((lead) => lead === value);
  return match ?? DEFAULT_NOTIFICATION_PREFS.leadMinutes;
}

/** Pure load: unknown leads, non-strings, malformed JSON and missing keys fall back to the
 * defaults per field - one bad value never rejects the other. */
export function loadNotificationPrefs(storage: NotificationPrefsStorage): NotificationPrefs {
  try {
    const raw = storage.getItem(NOTIFICATION_PREFS_STORAGE_KEY);
    if (raw === null) {
      return { ...DEFAULT_NOTIFICATION_PREFS };
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) {
      return { ...DEFAULT_NOTIFICATION_PREFS };
    }
    return {
      enabled: readEnabled(parsed['enabled']),
      leadMinutes: readLeadMinutes(parsed['leadMinutes']),
    };
  } catch {
    return { ...DEFAULT_NOTIFICATION_PREFS };
  }
}

/** Pure save; storage failures (private mode quota) are swallowed - the preferences are cosmetic. */
export function saveNotificationPrefs(prefs: NotificationPrefs, storage: NotificationPrefsStorage): void {
  try {
    storage.setItem(NOTIFICATION_PREFS_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // cosmetic preferences: ignore write failures
  }
}
